import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { filters, type InstanceApi } from '@eis/insuranceservice-sdk';
import { useApiClients } from './ApiClientsProvider';
import { apiKeys } from './keys';
import { newIdempotencyKey } from './jobs';
import { isSet, listQueryKey, pageOf, tarifSlugOf } from './list-query';
import { unwrap } from './result';
import { useServiceMutation, type ServiceMutation } from './useServiceMutation';

/**
 * The INSTANCE layer: Policies and Group Policies (architecture §A.2).
 *
 * Bitemporal, independent versions, resolve-at-read. The rule that shapes every invalidation
 * below: a change to a parent writes ZERO children and a change to a child writes ZERO parents
 * (decision D7). So a policy mutation invalidates that policy — never its master, never its
 * siblings — and a master mutation invalidates the master and the members PROJECTION, which is a
 * read-side view of the children rather than the children themselves.
 */

/**
 * The current belief about `validAt` — today when omitted. A policy that starts in the future
 * (one accepted from a lead, SDD-013 R-4) has no belief about today and answers 404 without a date,
 * so a link that knows the start passes it (`links.insurance.policy(code, { validAt })`).
 */
export function usePolicy(
  code: string,
  validAt?: string,
  /**
   * System time (SDD-017 §6): the belief as RECORDED at this instant (`…Z`) — the audit read.
   * Absent = now.
   */
  asOf?: string,
): UseQueryResult<InstanceApi.GetPolicyResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.policy(code, validAt, asOf),
    queryFn: () =>
      unwrap(
        instance.client.GET('/v1/policies/{code}', {
          params: {
            path: { code },
            ...(validAt === undefined && asOf === undefined
              ? {}
              : {
                  query: {
                    ...(validAt === undefined ? {} : { validAt }),
                    ...(asOf === undefined ? {} : { asOf }),
                  },
                }),
          },
        }),
      ),
    // A new cut of the SAME policy (another period, another recorded instant) keeps showing the
    // one on screen until it arrives — the timeline stays mounted and keeps the keyboard focus.
    // Another policy never shows the previous one's data.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey.includes(code) === true ? previous : undefined,
  });
}

/** The policy statuses a list filters by — the catalog's bare values (SDD-017 §5.2). */
export type PolicyStatusFilter = 'ACTIVE' | 'TERMINATED';
/** Standalone or a member of a Gruppen-Police — the catalog's bare values. */
export type PolicyMembershipFilter = 'STANDALONE' | 'MEMBER';

export interface PolicyFilter {
  /** Business date the beliefs are current about; empty = today. */
  readonly validAt?: string;
  /** A TARIF, not a version: `tarifservice:fahrrad`. Sent as the catalog's `tarifSlug`. */
  readonly tarifErn?: string;
  readonly status?: PolicyStatusFilter;
  readonly membership?: PolicyMembershipFilter;
  readonly parentPolicyCode?: string;
  readonly originLeadCode?: string;
  readonly pageSize?: number;
  /** Opaque cursor from the previous response. Never constructed by the console. */
  readonly pageToken?: string;
}

type ListPoliciesQuery = NonNullable<
  InstanceApi.paths['/v1/policies']['get']['parameters']['query']
>;

/**
 * The request a {@link PolicyFilter} means: the cut stays a parameter, every narrowing becomes a
 * `filters` clause built with the SDK builder (enum values bare, the tarif by slug).
 */
export function policyListQuery(filter: PolicyFilter): ListPoliciesQuery {
  const f = filters();
  const slug = tarifSlugOf(filter.tarifErn);
  if (slug !== undefined) f.eq('tarifSlug', slug);
  if (filter.status !== undefined) f.eq('status', filter.status);
  if (filter.membership !== undefined) f.eq('membership', filter.membership);
  if (isSet(filter.parentPolicyCode)) f.eq('parentPolicyCode', filter.parentPolicyCode);
  if (isSet(filter.originLeadCode)) f.eq('originLeadCode', filter.originLeadCode);
  return {
    ...(isSet(filter.validAt) ? { validAt: filter.validAt } : {}),
    ...(f.isEmpty() ? {} : { filters: f.toString() }),
    ...pageOf(filter),
  };
}

/**
 * The operations surface's entry point: current beliefs about one business date.
 *
 * A list, never a timeline. Filtering is SERVER-side — the filters are part of the contract
 * (`ListPoliciesRequest.filters`), not a predicate the console runs over a page it already has,
 * because a tenant with four thousand policies has to work the same way as one with two.
 */
export function usePolicies(
  filter: PolicyFilter = {},
): UseQueryResult<InstanceApi.ListPoliciesResponse> {
  const { instance } = useApiClients();
  const query = policyListQuery(filter);
  return useQuery({
    queryKey: apiKeys.instance.policies(listQueryKey(query)),
    queryFn: () => unwrap(instance.client.GET('/v1/policies', { params: { query } })),
  });
}

/**
 * The schema this policy's payload validates against, resolved THROUGH ITS PIN.
 *
 * `staleTime: Infinity`: published configuration is immutable, and the pin does not move under a
 * policy that is not being endorsed. Fetching it from the INSTANCE api rather than assembling it
 * from the configuration one is decision S-9 — the operations persona has no business there —
 * and it is also what keeps the console from taking an ERN apart to find a route.
 */
/** What `useCoverageAtDate` asks: the policy by code OR identifier, at a damage date (SDD-018 §7). */
export interface CoverageAtDateQuery {
  readonly policyCode?: string;
  /** `<identifier field>==<value>`, e.g. `data.bike.insurance_plate==123ABC`. */
  readonly identifier?: string;
  /** YYYY-MM-DD — the read's `validAt`. */
  readonly damageDate: string;
  readonly asOf?: string;
}

/**
 * Coverage at a damage date (SDD-018 §7, `GET /v1/policies:coverageAtDate`): the variant, the
 * categories and terms, the deductibles, the waiting period and a verdict — resolved through a
 * member's group policy at that date. Disabled until a policy and a date are named.
 */
export function useCoverageAtDate(query: CoverageAtDateQuery): UseQueryResult<InstanceApi.GetCoverageAtDateResponse> {
  const { instance } = useApiClients();
  const named = (query.policyCode ?? '') !== '' || (query.identifier ?? '') !== '';
  return useQuery({
    queryKey: apiKeys.instance.coverageAtDate(query),
    queryFn: () =>
      unwrap(
        instance.client.GET('/v1/policies:coverageAtDate', {
          params: {
            query: {
              damageDate: query.damageDate,
              ...(isSet(query.policyCode) ? { policyCode: query.policyCode } : {}),
              ...(isSet(query.identifier) ? { identifier: query.identifier } : {}),
              ...(isSet(query.asOf) ? { asOf: query.asOf } : {}),
            },
          },
        }),
      ),
    enabled: named && query.damageDate !== '',
  });
}

export function usePolicySchema(code: string, validAt?: string): UseQueryResult<InstanceApi.GetPolicySchemaResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.policySchema(code, validAt),
    queryFn: () =>
      unwrap(
        instance.client.GET('/v1/policies/{code}/schema', {
          params: { path: { code }, ...(validAt === undefined ? {} : { query: { validAt } }) },
        }),
      ),
    staleTime: Infinity,
  });
}

export function usePolicyTimeline(
  code: string,
  options?: { readonly enabled?: boolean },
): UseQueryResult<InstanceApi.GetPolicyTimelineResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.policyTimeline(code),
    queryFn: () =>
      unwrap(instance.client.GET('/v1/policies/{code}/timeline', { params: { path: { code } } })),
    enabled: options?.enabled ?? true,
  });
}

/**
 * One partner in one role — what a request carries and a snapshot stores (SDD-011 P-7). Never a
 * name: names resolve at read.
 */
export type PartnerLink = InstanceApi.PartnerLink;

export interface EndorsePolicyVariables {
  readonly code: string;
  readonly effectiveFrom: string;
  readonly data: Record<string, unknown>;
  /** The COMPLETE partner list from `effectiveFrom` on; omitted means unchanged (P-7). */
  readonly partners?: readonly PartnerLink[];
}

/**
 * An endorsement EXTENDS the timeline and moves `business_version` (decision S-8).
 *
 * The precondition rides on the ETag the SDK captured from the policy read, so two operators
 * endorsing the same policy is a conflict rather than a lost change — the race on an instance is
 * on the timeline head, not on a row (§B.10).
 */
export function useEndorsePolicy(): ServiceMutation<
  EndorsePolicyVariables,
  InstanceApi.EndorsePolicyResponse
> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code, effectiveFrom, data, partners }: EndorsePolicyVariables) =>
      unwrap(
        instance.client.POST('/v1/policies/{code}/endorsements', {
          params: { path: { code } },
          body: { effectiveFrom, changes: data, ...(partners ? { partners: [...partners] } : {}) },
        }),
      ),
    // This policy and its timeline. NOT its master: a change to a child writes zero parents,
    // so refetching the master here would be the console inventing a coupling the service
    // deliberately does not have (decision D7).
    invalidates: ({ code }) => [
      apiKeys.instance.policy(code),
      apiKeys.instance.policyTimeline(code),
      apiKeys.instance.policyPartnerHistory(code),
      apiKeys.instance.policyList(),
    ],
  });
}

export interface CorrectPolicyVariables {
  readonly code: string;
  readonly validFrom: string;
  readonly validTo?: string;
  readonly reason: string;
  readonly changes: Record<string, unknown>;
  /** The COMPLETE partner list the corrected segments carry; omitted means unchanged (P-7). */
  readonly partners?: readonly PartnerLink[];
}

/**
 * A correction SUPERSEDES beliefs over the same business time. `reason` is required — a
 * correction re-states history, and who did it and why is the audit record (decision S-7) — and
 * `business_version` does NOT move, which is why the response is read for its adjustment memo
 * rather than for a new version.
 */
export function useCorrectPolicy(): ServiceMutation<
  CorrectPolicyVariables,
  InstanceApi.CorrectPolicyResponse
> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code, validFrom, validTo, reason, changes, partners }: CorrectPolicyVariables) =>
      unwrap(
        instance.client.POST('/v1/policies/{code}/corrections', {
          params: { path: { code } },
          body: {
            validFrom,
            validTo,
            reason,
            changes,
            ...(partners ? { partners: [...partners] } : {}),
          },
        }),
      ),
    invalidates: ({ code }) => [
      apiKeys.instance.policy(code),
      apiKeys.instance.policyTimeline(code),
      apiKeys.instance.policyPartnerHistory(code),
      apiKeys.instance.policyList(),
    ],
  });
}

/**
 * The correction, run as a dry run: the segments it WOULD split and the premium it would move.
 *
 * A correction re-states history and raises an adjustment memo against real money, so it is the
 * one write on this surface an operator must be able to see before committing to. It writes
 * nothing — like the publish gate's `:validate`, and for the same reason.
 */
export function usePreviewPolicyCorrection(): ServiceMutation<
  CorrectPolicyVariables,
  InstanceApi.PreviewPolicyCorrectionResponse
> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code, validFrom, validTo, reason, changes, partners }: CorrectPolicyVariables) =>
      unwrap(
        instance.client.POST('/v1/policies/{code}/corrections:preview', {
          params: { path: { code } },
          body: {
            validFrom,
            validTo,
            reason,
            changes,
            ...(partners ? { partners: [...partners] } : {}),
          },
        }),
      ),
    // A preview writes nothing, so nothing about the policy changed. It still re-reads it: like
    // `:validate`, it is a POST, and the SDK's ETag middleware cannot tell a rehearsal from a
    // write — without the re-read the correction that follows would carry no precondition.
    invalidates: ({ code }) => [apiKeys.instance.policy(code)],
  });
}

export interface TerminatePolicyVariables {
  readonly code: string;
  /** The day cover stops. Dated, never "now": termination is a business-time event. */
  readonly effectiveFrom: string;
  readonly reason?: string;
}

/** Terminating a policy is an ENDORSEMENT with a different payload — it moves the head. */
export function useTerminatePolicy(): ServiceMutation<
  TerminatePolicyVariables,
  InstanceApi.TerminatePolicyResponse
> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code, effectiveFrom, reason }: TerminatePolicyVariables) =>
      unwrap(
        instance.client.POST('/v1/policies/{code}/terminate', {
          params: { path: { code } },
          body: { effectiveFrom, reason },
        }),
      ),
    invalidates: ({ code }) => [
      apiKeys.instance.policy(code),
      apiKeys.instance.policyTimeline(code),
      apiKeys.instance.policyList(),
    ],
  });
}

export interface DetachPolicyVariables {
  readonly code: string;
  readonly effectiveFrom: string;
  /** Required: a detached policy pays for itself, so it needs its own payment path. */
  readonly paymentMethodRef: string;
}

/**
 * Detachment is a ROLE PROMOTION, not a field update (service summary §4.1).
 *
 * Clear the parent pointer, promote the insured person to policyholder, collect a payment method,
 * flip billing to DIRECT, switch the document set, and flag the re-pricing that follows from
 * losing the group factor. The member keeps its tarif pins — it is the same cover, billed
 * differently — which is why this is one operation and not "edit the parent field".
 *
 * It touches ONE policy. The master is not written, and is not even read for the write: a change
 * to a child writes zero parents (decision D7).
 */
export function useDetachPolicy(): ServiceMutation<
  DetachPolicyVariables,
  InstanceApi.DetachPolicyResponse
> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code, effectiveFrom, paymentMethodRef }: DetachPolicyVariables) =>
      unwrap(
        instance.client.POST('/v1/policies/{code}/detach', {
          params: { path: { code } },
          body: { effectiveFrom, paymentMethodRef },
        }),
      ),
    invalidates: ({ code }) => [
      apiKeys.instance.policy(code),
      apiKeys.instance.policyTimeline(code),
      apiKeys.instance.policyList(),
    ],
  });
}

export interface CreatePolicyVariables {
  /** Empty means the service mints one — no id is ever invented on the console (rule 8b). */
  readonly code?: string;
  /** Set to enrol as a member of a Group Policy; omitted for a standalone policy. */
  readonly parentPolicyCode?: string;
  /** The tarif IDENTITY, `tarifservice:fahrrad`; the version comes from the pin, never from here. */
  readonly tarifErn: string;
  readonly effectiveFrom: string;
  readonly data: Record<string, unknown>;
  /**
   * The policy's partners per role, validated against the pinned version's `partnerRoles`
   * (SDD-011 P-8). A member carries no policyholder link — it inherits the master's (D7).
   */
  readonly partners: readonly PartnerLink[];
}

/**
 * Creation carries no precondition — there is no prior belief to quote. A refusal for the partner
 * list arrives as VALIDATION_FAILED with `/partners/<i>/…` pointers (read them with
 * `validationIssuesOf`), naming the pinned version's rule that spoke.
 */
export function useCreatePolicy(): ServiceMutation<
  CreatePolicyVariables,
  InstanceApi.CreatePolicyResponse
> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ partners, ...rest }: CreatePolicyVariables) =>
      unwrap(instance.client.POST('/v1/policies', { body: { ...rest, partners: [...partners] } })),
    // The list, and — for a member — the master's member projection: a new member changes what
    // the master's Policies tab shows, and nothing else about the master (D7).
    invalidates: ({ parentPolicyCode }) => [
      apiKeys.instance.policyList(),
      ...(parentPolicyCode ? [apiKeys.instance.groupPolicyMembers(parentPolicyCode)] : []),
    ],
  });
}

/**
 * Who filled which role, and when (SDD-011 P-7): DERIVED from the timeline by coalescing
 * consecutive segments with the same partner in the same role. Cached under the policy, so a
 * write that extends or re-states the timeline drops it with the timeline.
 */
export function usePolicyPartnerHistory(
  code: string,
): UseQueryResult<InstanceApi.GetPolicyPartnerHistoryResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.policyPartnerHistory(code),
    queryFn: () =>
      unwrap(
        instance.client.GET('/v1/policies/{code}/partner-history', {
          params: { path: { code } },
        }),
      ),
  });
}

/**
 * What a member enrolled under the agreement is validated against — the child version's schema
 * and partner roles, resolved THROUGH the agreement's pin (SDD-011 P-8). The enrol dialog is
 * generated from it, so it asks for exactly what the create call will require. Immutable once
 * published, hence never stale.
 */
export function useGroupPolicyMemberSchema(
  code: string,
): UseQueryResult<InstanceApi.GetGroupPolicyMemberSchemaResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.groupPolicyMemberSchema(code),
    queryFn: () =>
      unwrap(
        instance.client.GET('/v1/group-policies/{code}/member-schema', {
          params: { path: { code } },
        }),
      ),
    staleTime: Infinity,
  });
}

export function useGroupPolicyPartnerHistory(
  code: string,
): UseQueryResult<InstanceApi.GetGroupPolicyPartnerHistoryResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.groupPolicyPartnerHistory(code),
    queryFn: () =>
      unwrap(
        instance.client.GET('/v1/group-policies/{code}/partner-history', {
          params: { path: { code } },
        }),
      ),
  });
}

/** The Group Policy statuses a list filters by — the catalog's bare values. */
export type GroupPolicyStatusFilter = 'IN_NEGOTIATION' | 'ACTIVE' | 'TERMINATED';

export interface GroupPolicyFilter {
  /** Business date the beliefs are current about; empty = today. */
  readonly validAt?: string;
  readonly status?: GroupPolicyStatusFilter;
  /** The standard search (SDD-017 §8): the code, the holder's name, the searchable fields. */
  readonly search?: string;
  /** Sent as the catalog's `holderPartnerId`. */
  readonly policyholderPartnerId?: string;
  readonly pageSize?: number;
  /** Opaque cursor from the previous response. Never constructed by the console. */
  readonly pageToken?: string;
}

type ListGroupPoliciesQuery = NonNullable<
  InstanceApi.paths['/v1/group-policies']['get']['parameters']['query']
>;

export function groupPolicyListQuery(filter: GroupPolicyFilter): ListGroupPoliciesQuery {
  const f = filters();
  if (filter.status !== undefined) f.eq('status', filter.status);
  if (isSet(filter.policyholderPartnerId)) f.eq('holderPartnerId', filter.policyholderPartnerId);
  return {
    ...(isSet(filter.validAt) ? { validAt: filter.validAt } : {}),
    ...(f.isEmpty() ? {} : { filters: f.toString() }),
    ...(isSet(filter.search) ? { search: filter.search } : {}),
    ...pageOf(filter),
  };
}

/**
 * The Gruppen-Policen surface's entry point: current beliefs about one business date.
 *
 * Search, status filter and paging are SERVER-side, against `ListGroupPoliciesRequest`. A
 * client-side predicate over a page the console already holds would filter one page and call it
 * a result, and it would have to be torn out the first time a tenant has four thousand Group
 * Policies.
 *
 * The member count and the policyholder's NAME arrive on the row. Neither can be derived here:
 * the count is an aggregate over the member projection, and the name comes from a domain this
 * platform does not own — deriving either client-side would be one request per row.
 */
export function useGroupPolicies(
  filter: GroupPolicyFilter = {},
): UseQueryResult<InstanceApi.ListGroupPoliciesResponse> {
  const { instance } = useApiClients();
  const query = groupPolicyListQuery(filter);
  return useQuery({
    queryKey: apiKeys.instance.groupPolicies(listQueryKey(query)),
    queryFn: () => unwrap(instance.client.GET('/v1/group-policies', { params: { query } })),
  });
}

/**
 * The group tarifs a Gruppen-Police can actually be created under, with the tarifs each one
 * allows as members.
 *
 * It reads the INSTANCE api, not the configuration one. An ERN is an identity and not a route,
 * and the operations persona has no business on the configuration API at all (service summary
 * §6, SSOT I-6) — the precedent is `GetPolicySchema`, which lives on PolicyService for the same
 * reason. Granting `groupTariffs.view` instead would not have filled this dropdown: nav entries
 * and route guards share one declaration (Addendum A.2), so the grant would have put the whole
 * configuration surface in the operations sidebar. See OQ-11.
 *
 * It carries a name and an ERN and nothing else. There is no version to render here.
 */
export function useAvailableGroupTarifs(): UseQueryResult<InstanceApi.ListAvailableGroupTarifsResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.availableGroupTarifs(),
    queryFn: () => unwrap(instance.client.GET('/v1/group-policies:available-group-tarifs')),
  });
}

export function useGroupPolicy(code: string): UseQueryResult<InstanceApi.GetGroupPolicyResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.groupPolicy(code),
    queryFn: () =>
      unwrap(instance.client.GET('/v1/group-policies/{code}', { params: { path: { code } } })),
    // A screen that MAY have a master (a lead's detail) asks with '' when it has none.
    enabled: code !== '',
  });
}

/**
 * Who is a member ON A BUSINESS DATE — the claims question, and the reason `activeOn` is a
 * parameter rather than a default.
 *
 * Omitted, the service answers about today, and a member whose cover starts next month is
 * simply absent. That is correct for the question and wrong for a screen that calls itself an
 * inventory: a Group Policy whose members all start on the first of next month renders as
 * empty, while the Leads tab beside it links to the very policies that are missing. The policy
 * page learned the same lesson at R-52; this is it one level up.
 */
export function useGroupPolicyMembers(
  code: string,
  activeOn?: string,
): UseQueryResult<InstanceApi.ListGroupPolicyMembersResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.groupPolicyMembers(code, activeOn),
    queryFn: () =>
      unwrap(
        instance.client.GET('/v1/group-policies/{code}/members', {
          params: {
            path: { code },
            ...(activeOn === undefined ? {} : { query: { activeOn } }),
          },
        }),
      ),
  });
}

export function useGroupPolicyTimeline(
  code: string,
): UseQueryResult<InstanceApi.GetGroupPolicyTimelineResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.groupPolicyTimeline(code),
    queryFn: () =>
      unwrap(
        instance.client.GET('/v1/group-policies/{code}/timeline', { params: { path: { code } } }),
      ),
  });
}

export interface CreateGroupPolicyVariables {
  readonly code?: string;
  /**
   * DEPRECATED shorthand for a `partners` entry in the policyholder role (SDD-011 P-9); honoured
   * one release. New callers send `partners`.
   */
  readonly policyholderPartnerId?: string;
  /** The agreement's partners per role, validated against the group version's roles (P-8). */
  readonly partners?: readonly PartnerLink[];
  /** The group tarif. Operations never types a version number (architecture §A.5). */
  readonly groupTarifErn: string;
  readonly allowedTarifs: readonly string[];
  readonly billing: InstanceApi.BillingConfig;
  readonly effectiveFrom: string;
  readonly data: Record<string, unknown>;
}

/**
 * Creation opens the agreement IN_NEGOTIATION and freezes the group tarif's latest active version
 * onto it. It carries no precondition — there is no prior belief to quote — which is why it is
 * the one write on this surface that cannot conflict.
 */
export function useCreateGroupPolicy(): ServiceMutation<
  CreateGroupPolicyVariables,
  InstanceApi.CreateGroupPolicyResponse
> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ allowedTarifs, partners, ...rest }: CreateGroupPolicyVariables) =>
      // `allowedTarifs` is copied rather than passed through: the contract's body type is
      // mutable, and handing it a readonly array is the one thing the generated types refuse.
      unwrap(
        instance.client.POST('/v1/group-policies', {
          body: {
            ...rest,
            allowedTarifs: [...allowedTarifs],
            ...(partners === undefined ? {} : { partners: [...partners] }),
          },
        }),
      ),
    // Only the list: a Group Policy that did not exist has no detail entry to drop.
    invalidates: () => [apiKeys.instance.groupPolicyList()],
  });
}

export interface ActivateGroupPolicyVariables {
  readonly code: string;
  readonly effectiveFrom: string;
}

/**
 * in_negotiation → active. A transition, not a PATCHed field, because it has side effects
 * (service summary §5.4) — and an ENDORSEMENT, so the precondition rides on the ETag the SDK
 * captured from the read that showed the operator the agreement they are activating.
 */
export function useActivateGroupPolicy(): ServiceMutation<
  ActivateGroupPolicyVariables,
  InstanceApi.ActivateGroupPolicyResponse
> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code, effectiveFrom }: ActivateGroupPolicyVariables) =>
      unwrap(
        instance.client.POST('/v1/group-policies/{code}:activate', {
          params: { path: { code } },
          body: { effectiveFrom },
        }),
      ),
    invalidates: ({ code }) => [
      apiKeys.instance.groupPolicy(code),
      apiKeys.instance.groupPolicyTimeline(code),
      apiKeys.instance.groupPolicyList(),
    ],
  });
}

export interface UpdateGroupPolicyVariables {
  readonly code: string;
  /** The day the new terms take effect. Dated, never "now" — this is business time. */
  readonly effectiveFrom: string;
  readonly allowedTarifs?: readonly string[];
  readonly billing?: InstanceApi.BillingConfig;
  readonly data?: Record<string, unknown>;
  /** The COMPLETE partner list from `effectiveFrom` on; omitted means unchanged (SDD-011 P-7). */
  readonly partners?: readonly PartnerLink[];
}

/**
 * A new agreement version: ONE master snapshot and ZERO member writes (decisions D5/D7).
 *
 * The invalidation says exactly that. The master, its timeline and the member PROJECTION — a
 * read-side view whose rows resolve inherited values through the master — are refetched, and no
 * member's own cache entry is touched, because no member changed.
 */
export function useUpdateGroupPolicy(): ServiceMutation<
  UpdateGroupPolicyVariables,
  InstanceApi.UpdateGroupPolicyResponse
> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code, allowedTarifs, partners, ...rest }: UpdateGroupPolicyVariables) =>
      unwrap(
        instance.client.PUT('/v1/group-policies/{code}', {
          params: { path: { code } },
          body: {
            ...rest,
            ...(allowedTarifs === undefined ? {} : { allowedTarifs: [...allowedTarifs] }),
            ...(partners === undefined ? {} : { partners: [...partners] }),
          },
        }),
      ),
    invalidates: ({ code }) => [
      apiKeys.instance.groupPolicy(code),
      apiKeys.instance.groupPolicyTimeline(code),
      apiKeys.instance.groupPolicyPartnerHistory(code),
      apiKeys.instance.groupPolicyMembers(code),
      apiKeys.instance.groupPolicyList(),
    ],
  });
}

export interface TerminateGroupPolicyVariables {
  readonly code: string;
  /** RFC 3339 full-date: the day the Group Policy stops being in force. */
  readonly effectiveFrom: string;
  readonly reason?: string;
}

/**
 * Terminating the master does NOT cascade by default (SSOT §5.4).
 *
 * The invalidation says the same thing the service does: the master and its members PROJECTION
 * are refetched, and no child policy's cache entry is touched, because none of them changed.
 */
export function useTerminateGroupPolicy(): ServiceMutation<
  TerminateGroupPolicyVariables,
  InstanceApi.TerminateGroupPolicyResponse
> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code, effectiveFrom, reason }: TerminateGroupPolicyVariables) =>
      unwrap(
        instance.client.POST('/v1/group-policies/{code}:terminate', {
          params: { path: { code } },
          body: { effectiveFrom, reason },
        }),
      ),
    // The master, its timeline and the members PROJECTION — which is a read-side view, not the
    // children. No child policy's cache entry is touched, because no child changed.
    invalidates: ({ code }) => [
      apiKeys.instance.groupPolicy(code),
      apiKeys.instance.groupPolicyTimeline(code),
      apiKeys.instance.groupPolicyMembers(code),
      apiKeys.instance.groupPolicyList(),
    ],
  });
}

// ---------------------------------------------------------------------------------------------
// LEADS (SDD-013): the offer-to-policy pipeline.
//
// A lead is a HEAD with an append-only history (T-24), not a bitemporal timeline: `version` is the
// head's row version, quoted back as If-Match by the SDK from the ETag the read returned, exactly
// as a policy's business version is. So every lead mutation invalidates the lead, its versions and
// the lists — and nothing about a policy, until AcceptLead creates one (then the policy list and,
// for a member lead, the master's members projection too: a new member changes what the master's
// Policies tab shows, and nothing else about the master — D7).
//
// The 409 reasons a lead mutation may answer with — LEAD_LOCKED, LEAD_NOT_ACCEPTED, LEAD_PIN_STALE,
// TRANSITION_*, FOUR_EYES, PARTNER_DRAFT_INVALID — pass through as ordinary errors with their reason
// (`refusalReasonOf`), the ASSET_NOT_EDITABLE precedent (SDD-010 R-14). Only the concurrency
// vocabulary (412/428) becomes a `conflict`.
// ---------------------------------------------------------------------------------------------

/** The platform's lead status kinds — the catalog's bare values. */
export type LeadStatusKindFilter = 'OPEN' | 'ACCEPTED' | 'CONVERTED' | 'CLOSED';

export interface LeadFilter {
  /** A TARIF, not a version: `tarifservice:fahrrad`. Sent as the catalog's `tarifSlug`. */
  readonly tarifErn?: string;
  /** The tenant's status code, exact — or the platform's kind. */
  readonly statusCode?: string;
  readonly statusKind?: LeadStatusKindFilter;
  readonly membership?: PolicyMembershipFilter;
  /** One master's leads; implies MEMBER. */
  readonly parentPolicyCode?: string;
  /** The standard search (SDD-017 §8): the code, the names and the searchable fields. */
  readonly search?: string;
  /** RFC 3339 full-date: leads created on or after its start (UTC). */
  readonly createdAfter?: string;
  readonly pageSize?: number;
  /** Opaque cursor from the previous response. Never constructed by the console. */
  readonly pageToken?: string;
}

type ListLeadsQuery = NonNullable<InstanceApi.paths['/v1/leads']['get']['parameters']['query']>;

export function leadListQuery(filter: LeadFilter): ListLeadsQuery {
  const f = filters();
  const slug = tarifSlugOf(filter.tarifErn);
  if (slug !== undefined) f.eq('tarifSlug', slug);
  if (isSet(filter.statusCode)) f.eq('statusCode', filter.statusCode);
  if (filter.statusKind !== undefined) f.eq('statusKind', filter.statusKind);
  if (filter.membership !== undefined) f.eq('membership', filter.membership);
  if (isSet(filter.parentPolicyCode)) f.eq('parentPolicyCode', filter.parentPolicyCode);
  if (isSet(filter.createdAfter)) f.ge('createdAt', `${filter.createdAfter}T00:00:00Z`);
  return {
    ...(f.isEmpty() ? {} : { filters: f.toString() }),
    ...(isSet(filter.search) ? { search: filter.search } : {}),
    ...pageOf(filter),
  };
}

/** Head rows, filtered SERVER-side (SDD-013 §7.1); a page, never a history. */
export function useLeads(filter: LeadFilter = {}): UseQueryResult<InstanceApi.ListLeadsResponse> {
  const { instance } = useApiClients();
  const query = leadListQuery(filter);
  return useQuery({
    queryKey: apiKeys.instance.leads(listQueryKey(query)),
    queryFn: () => unwrap(instance.client.GET('/v1/leads', { params: { query } })),
  });
}

/** The head with the transitions it may take from where it is, resolved through ITS pin. */
export function useLead(code: string): UseQueryResult<InstanceApi.GetLeadResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.lead(code),
    queryFn: () => unwrap(instance.client.GET('/v1/leads/{code}', { params: { path: { code } } })),
  });
}

/**
 * The schema, the roles AND the lifecycle this lead is written against, through its pin
 * (SDD-013 §6.4). Immutable configuration: cached forever, for the reason `usePolicySchema` is.
 */
export function useLeadSchema(code: string): UseQueryResult<InstanceApi.GetLeadSchemaResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.leadSchema(code),
    queryFn: () =>
      unwrap(instance.client.GET('/v1/leads/{code}/schema', { params: { path: { code } } })),
    staleTime: Infinity,
  });
}

/** The audit history, oldest first (SDD-013 §5.6; `leads.view` suffices, Q-9). */
export function useLeadVersions(code: string): UseQueryResult<InstanceApi.ListLeadVersionsResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.leadVersions(code),
    queryFn: () =>
      unwrap(instance.client.GET('/v1/leads/{code}/versions', { params: { path: { code } } })),
  });
}

/** One recorded version — the complete head as it was; never rewritten, so cached forever. */
export function useLeadVersion(
  code: string,
  version: string,
): UseQueryResult<InstanceApi.GetLeadVersionResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.leadVersion(code, version),
    queryFn: () =>
      unwrap(
        instance.client.GET('/v1/leads/{code}/versions/{version}', {
          params: { path: { code, version } },
        }),
      ),
    enabled: version !== '',
    staleTime: Infinity,
  });
}

/** The tarifs a STANDALONE lead can be opened under today — the twin of `useAvailableGroupTarifs`. */
export function useAvailableTarifs(): UseQueryResult<InstanceApi.ListAvailableTarifsResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.availableTarifs(),
    queryFn: () => unwrap(instance.client.GET('/v1/leads:available-tarifs')),
  });
}

/** An import job as it was recorded (SDD-013 §7.3). Written once, so cached forever. */
export function useLeadImportJob(jobId: string): UseQueryResult<InstanceApi.GetLeadImportJobResponse> {
  const { instance } = useApiClients();
  return useQuery({
    queryKey: apiKeys.instance.leadImport(jobId),
    queryFn: () =>
      unwrap(instance.client.GET('/v1/lead-imports/{jobId}', { params: { path: { jobId } } })),
    enabled: jobId !== '',
    staleTime: Infinity,
  });
}

/** `CreateLeadRequest` as the console sends it: `policy` IS a create-policy request (SDD-013 §5.1). */
export type CreateLeadVariables = InstanceApi.CreateLeadRequest;

/** Opens a lead. A root-create: no precondition (T-12), the code is minted unless supplied. */
export function useCreateLead(): ServiceMutation<CreateLeadVariables, InstanceApi.CreateLeadResponse> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: (variables: CreateLeadVariables) =>
      unwrap(instance.client.POST('/v1/leads', { body: variables })),
    invalidates: () => [apiKeys.instance.leadList()],
  });
}

export interface UpdateLeadVariables {
  readonly code: string;
  /** The COMPLETE body (SDD-013 §5.1): a version is complete or it is not a version. */
  readonly policy: InstanceApi.CreatePolicyRequest;
  readonly partnerDrafts?: readonly InstanceApi.PartnerDraft[];
  readonly effectiveFrom?: string;
  readonly validUntil?: string;
}

/** The queries one lead's write touches: the head, its history, and every list it may be in. */
const leadWriteKeys = (code: string) => [
  apiKeys.instance.lead(code),
  apiKeys.instance.leadVersions(code),
  apiKeys.instance.leadList(),
];

/** A new version through the lead's FROZEN pin (D-L3). Refused with LEAD_LOCKED unless OPEN and unlocked. */
export function useUpdateLead(): ServiceMutation<UpdateLeadVariables, InstanceApi.UpdateLeadResponse> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code, policy, partnerDrafts, effectiveFrom, validUntil }: UpdateLeadVariables) =>
      unwrap(
        instance.client.PUT('/v1/leads/{code}', {
          params: { path: { code } },
          body: {
            policy,
            ...(partnerDrafts ? { partnerDrafts: [...partnerDrafts] } : {}),
            ...(effectiveFrom === undefined ? {} : { effectiveFrom }),
            ...(validUntil === undefined ? {} : { validUntil }),
          },
        }),
      ),
    invalidates: ({ code }) => leadWriteKeys(code),
  });
}

export interface TransitionLeadVariables {
  readonly code: string;
  /** The target status's code — one of the lead's `availableTransitions`. */
  readonly to: string;
  /** Required when the transition is marked `reasonRequired`. */
  readonly reason?: string;
}

/** Moves the lead along its pinned lifecycle (SDD-013 §6.2); the four rules are the service's. */
export function useTransitionLead(): ServiceMutation<
  TransitionLeadVariables,
  InstanceApi.TransitionLeadResponse
> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code, to, reason }: TransitionLeadVariables) =>
      unwrap(
        instance.client.POST('/v1/leads/{code}:transition', {
          params: { path: { code } },
          body: { to, ...(reason === undefined ? {} : { reason }) },
        }),
      ),
    invalidates: ({ code }) => leadWriteKeys(code),
  });
}

export interface RefreshLeadVariables {
  readonly code: string;
}

/** Re-pins to what a create today would resolve and re-rates (D-L3) — the explicit answer to a stale pin. */
export function useRefreshLead(): ServiceMutation<RefreshLeadVariables, InstanceApi.RefreshLeadResponse> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code }: RefreshLeadVariables) =>
      unwrap(instance.client.POST('/v1/leads/{code}:refresh', { params: { path: { code } }, body: {} })),
    invalidates: ({ code }) => leadWriteKeys(code),
  });
}

export interface AcceptLeadVariables {
  readonly code: string;
  /** Optional; the service mints `P-<year>-<6>` when empty. */
  readonly policyCode?: string;
  /** D-L3: proceed although the pin is stale; the policy is created under the FRESH pin. */
  readonly acceptStalePin?: boolean;
  /**
   * Not sent. The master whose members projection the accept changes, when the lead is a member
   * lead — so the Policies tab of the agreement refetches (D7: the projection, never the master).
   */
  readonly parentPolicyCode?: string;
}

/**
 * Turns an ACCEPTED lead into a policy (SDD-013 §7.2). One transaction on the service; here the
 * lead, its history, both lists and — for a member — the master's members projection reload.
 */
export function useAcceptLead(): ServiceMutation<AcceptLeadVariables, InstanceApi.AcceptLeadResponse> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code, policyCode, acceptStalePin }: AcceptLeadVariables) =>
      unwrap(
        instance.client.POST('/v1/leads/{code}:accept', {
          params: { path: { code } },
          body: {
            ...(policyCode === undefined ? {} : { policyCode }),
            ...(acceptStalePin === undefined ? {} : { acceptStalePin }),
          },
        }),
      ),
    invalidates: ({ code, parentPolicyCode }) => [
      ...leadWriteKeys(code),
      apiKeys.instance.policyList(),
      ...(parentPolicyCode ? [apiKeys.instance.groupPolicyMembers(parentPolicyCode)] : []),
    ],
  });
}

export interface RegisterLeadPartnersVariables {
  readonly code: string;
}

/** Sends every draft partner to the Partner domain and stores the links (D-L4 ii, P-12). */
export function useRegisterLeadPartners(): ServiceMutation<
  RegisterLeadPartnersVariables,
  InstanceApi.RegisterLeadPartnersResponse
> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ code }: RegisterLeadPartnersVariables) =>
      unwrap(
        instance.client.POST('/v1/leads/{code}/partners:register', {
          params: { path: { code } },
          body: {},
        }),
      ),
    invalidates: ({ code }) => leadWriteKeys(code),
  });
}

/**
 * `ImportLeadsRequest` as the console sends it: the JSON `items` of SDD-013, or since SDD-020 a
 * `file` (CSV, XLSX, JSON — base64 `content`), or `fromDryRunJobId` to import what a dry run read,
 * with `wait`, `label` and `locale`.
 */
export type ImportLeadsVariables = InstanceApi.ImportLeadsRequest;

/**
 * Bulk enrollment (SDD-013 §7.3, SDD-020 §7.5): every import runs as a job — 202 with the job (and
 * the record once it finished within `wait`), 200 with a `note` when the key or the file named an
 * earlier job. A dry run writes no lead, so it invalidates only the caller's job list. The create
 * carries an `Idempotency-Key`, so the SDK may retry it after a dropped connection.
 */
export function useImportLeads(): ServiceMutation<ImportLeadsVariables, InstanceApi.ImportLeadsResponse> {
  const { instance } = useApiClients();
  return useServiceMutation({
    mutationFn: (variables: ImportLeadsVariables) =>
      unwrap(
        instance.client.POST('/v1/leads:import', {
          body: variables,
          headers: { 'Idempotency-Key': newIdempotencyKey() },
        }),
      ),
    invalidates: ({ dryRun }) =>
      dryRun === true
        ? [apiKeys.jobs.mine('insurance')]
        : [apiKeys.instance.leadList(), apiKeys.jobs.mine('insurance')],
  });
}
